/**
 * /admin/forgot-password — operator self-service password reset.
 *
 * POSTs to `/v1/auth/password-reset-request` with the operator's
 * email. The backend is intentionally idempotent + email-enumeration-
 * defensive — it always returns 200 whether the address exists or
 * not, so we always show the same success message. An operator who
 * typed the wrong email doesn't get told "no such account", they
 * just notice the email never arrives and try again.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { OrbotoLockup } from '../components/OrbotoLockup';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api('/v1/auth/password-reset-request', {
        method: 'POST',
        body: { email },
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please try again in a minute.');
      } else {
        // Backend always returns 200 — reaching this branch means a
        // network failure on the client side. Tell the user something
        // concrete enough to retry without leaking which case it was.
        setError('Could not send the reset email. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">Control Plane — Reset password</div>
        </div>

        {submitted ? (
          <div className="flex flex-col gap-4">
            <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-3 text-sm text-emerald-800 dark:text-emerald-300">
              If that email matches an operator account, a password-reset link
              is on its way. Check your inbox.
            </div>
            <Link
              to="/login"
              className="text-center text-sm text-brand-500 hover:text-brand-600"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter your operator email address and we&apos;ll send you a link
              to set a new password.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">Email</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>

            {error && (
              <div
                role="alert"
                className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>

            <Link
              to="/login"
              className="text-center text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
